import React, { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Badge, Button, Checkbox, Dropdown, Empty, Input, Modal, Popover, Select, Space, Switch, Table, Tag, Tooltip, Typography } from 'antd';
import { Eye, EyeOff, Funnel, LayoutGrid } from 'lucide-react';
import { DownloadOutlined, ExclamationCircleOutlined, ReloadOutlined, SearchOutlined, SettingOutlined } from '@ant-design/icons';
import { useTheme } from '@/App';
import { defaultColors } from '@/themes/antd-theme';

/**
 * DataTable — the one table component for the app.
 *
 * Wraps antd <Table> with every convention the app had spread across TableContainer,
 * useTableConfig + TableColumnConfig, TableInModal and 130 hand-rolled <Table>s:
 *   - fixed-height rows box (`height`) with the pagination bar pinned below it, or `height={null}`
 *     to grow with content; `minHeight` floor
 *   - toolbar: title / row count, then — each opt-in, all off by default — search (`searchable`, client-side across
 *     `searchKeys` or all visible dataIndexes), a funnel icon (`filterPanel`: a multi-select per filterable column)
 *     and the settings icon (`columnConfig`; on with a `tableKey`) that opens a column search + checkboxes,
 *     honouring col.defaultVisible / col.alwaysVisible,
 *     CSV / XLSX export of the *filtered* rows, refresh, and `toolbarExtra` for page buttons
 *   - pagination on by default: showTotal, size changer, page size persisted per `tableKey`
 *   - skeleton rows on first load, centred empty state, thin themed scrollbars, dark mode
 *   - sorting on every data column (type inferred: number / date / string; `sortType` overrides; `sortable: false` on a
 *     column or `sortable={false}` on the table turns it off) and value filters with search on columns that have
 *     ≤ `maxFilterValues` distinct values (or `filterable: true`); active filters show as chips with a clear control
 *   - one width strategy for every table (see `useTableGeometry`): the container is measured, `%` widths are
 *     resolved to pixels against it, `scrollX` is reduced to the exact pixel width the columns need (or dropped
 *     when they already fit), and `tableLayout: 'fixed'` is applied once every column has a width — so the
 *     header, the body and the summary row always share one colgroup and stay in step
 *   - `scrollX` is auto ('max-content') when any column is `fixed` or `scrollX` is passed;
 *     suppressed when empty so an empty table never shows a scrollbar
 *   - everything else (`rowSelection`, `expandable`, `onRow`, `summary`, `rowClassName`,
 *     `size`, `sticky`, `onChange`…) passes straight through to antd; `bordered` (default on) draws the lines between columns
 *
 * - empty / error: `emptyMessage` ("No products found") names the module; with a search or filter active the
 *   message becomes "No products match your search or filters" (override with `emptyFilteredMessage`).
 *   `error` (true or any message — the text is never shown raw) + `onRetry` swap the empty state for a
 *   "couldn't load" state with a Retry button, so a failed call never reads as "no data".
 * - `serverSide`: the parent owns paging (and, with `sorter: true` / `filters` on a column, sorting and
 *   filtering) and feeds one page of rows. Pass `pagination={{ current, pageSize, total, onChange }}`; the
 *   table then never re-slices, never resets the page on its own, does not auto-sort/-filter the page
 *   (that would reorder one page only) and shows `total` in the toolbar. `onSearch` is the server search hook.
 *
 * `data` and `dataSource` are interchangeable; `columns` is the antd column array plus the optional
 * `defaultVisible`, `alwaysVisible`, `sortType` ('string'|'number'|'date'), `exportable`,
 * `exportValue(record)` keys.
 */

const PAGINATION_RESERVED_HEIGHT = 56;
const SKELETON_ROW_COUNT = 6;
const DEFAULT_PAGE_SIZE = 15;
const PAGE_SIZE_OPTIONS = ['10', '15', '20', '50', '100'];

const storage = {
  get(k, fallback) { try { const v = localStorage.getItem(k); return v == null ? fallback : JSON.parse(v); } catch (e) { return fallback; } },
  set(k, v) { try { localStorage.setItem(k, JSON.stringify(v)); } catch (e) { /* ignore */ } },
};

const cellText = (v) => {
  if (v == null) return '';
  if (typeof v === 'object') { if (Array.isArray(v)) return v.map(cellText).join(', '); if (v.$isDayjsObject || v._isAMomentObject) return v.format?.('YYYY-MM-DD HH:mm') || ''; return JSON.stringify(v); }
  return String(v);
};
const getByIndex = (rec, di) => (Array.isArray(di) ? di.reduce((o, k) => (o == null ? o : o[k]), rec) : rec?.[di]);
const colKey = (c, i) => c.key ?? (Array.isArray(c.dataIndex) ? c.dataIndex.join('.') : c.dataIndex) ?? `col_${i}`;
const colTitle = (c) => (typeof c.title === 'string' ? c.title : typeof c.title === 'function' ? colKey(c) : c.exportTitle || colKey(c));

const sorterFor = (type, di) => (a, b) => {
  const x = getByIndex(a, di), y = getByIndex(b, di);
  if (x == null && y == null) return 0; if (x == null) return 1; if (y == null) return -1;
  if (type === 'number') return Number(x) - Number(y);
  if (type === 'date') return new Date(x).getTime() - new Date(y).getTime();
  return String(x).localeCompare(String(y), undefined, { numeric: true, sensitivity: 'base' });
};

const inferSortType = (rows, di) => {
  let num = 0, date = 0, seen = 0;
  for (const r of rows.slice(0, 50)) {
    const v = getByIndex(r, di); if (v == null || v === '') continue; seen += 1;
    if (typeof v === 'number' || (typeof v === 'string' && v.trim() !== '' && !Number.isNaN(Number(v)))) num += 1;
    else if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}/.test(v) && !Number.isNaN(Date.parse(v))) date += 1;
  }
  if (!seen) return 'string'; if (num === seen) return 'number'; if (date === seen) return 'date'; return 'string';
};
/** Header text of a column — a node title (e.g. wrapped in a Tooltip) falls back to `exportTitle`. */
const titleText = (c) => (typeof c.title === 'string' ? c.title : typeof c.title === 'function' ? '' : c.exportTitle || '');
const isActionColumn = (c) => c.dataIndex == null || /action|operation|^\s*$/i.test(titleText(c));

/* ---------------------------------------------------------------------------
 * Column geometry
 *
 * antd renders the header, the body and the summary as *separate* tables as soon as `scroll.y` is set
 * (which is the DataTable default, because of `height`). They only line up while every one of them
 * resolves its colgroup to the same pixel widths. Percentage widths break that (they resolve against
 * different widths once the body owns a scrollbar, and they silently renormalise when they don't sum
 * to 100), and `scrollX: 'max-content'` drops antd into `table-layout: auto`, where the declared
 * widths become hints and the content decides — which is where the stray horizontal scrollbars and
 * the clipped action columns come from. So: measure the container, turn every width into pixels, and
 * ask for a horizontal scrollbar only for the pixels the columns actually need.
 * ------------------------------------------------------------------------ */
const PCT_WIDTH = /^\s*(\d+(?:\.\d+)?)\s*%\s*$/;
const PX_WIDTH = /^\s*(\d+(?:\.\d+)?)\s*(?:px)?\s*$/;
const AUTO_COL_WIDTH = 120;   // assumed width of a column that declares none
const GUTTER_COL_WIDTH = 40;  // antd's checkbox / expand gutter columns
const MIN_COL_WIDTH = 48;
const asPct = (w) => { const m = typeof w === 'string' ? PCT_WIDTH.exec(w) : null; return m ? parseFloat(m[1]) : null; };
const asPx = (w) => {
  if (typeof w === 'number' && Number.isFinite(w)) return w;
  const m = typeof w === 'string' ? PX_WIDTH.exec(w) : null;
  return m ? parseFloat(m[1]) : null;
};

/** Width of the element the table renders into, tracked live (laptop widths, sider collapse, splitters). */
function useContainerWidth(ref) {
  const [width, setWidth] = useState(0);
  const read = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const next = Math.round(el.getBoundingClientRect().width);
    setWidth((prev) => (Math.abs(prev - next) < 1 ? prev : next)); // same value ⇒ same state ⇒ no extra render
  }, [ref]);
  // Deliberately three sources: after *every* render (mount, a tab becoming visible, new columns), on
  // window resize, and from a ResizeObserver for the changes neither sees (sider collapse, split panes).
  // The observer alone is not enough — some hosts never deliver it.
  useLayoutEffect(read);
  useEffect(() => {
    window.addEventListener('resize', read);
    let ro;
    try { if (typeof ResizeObserver !== 'undefined' && ref.current) { ro = new ResizeObserver(read); ro.observe(ref.current); } } catch (e) { /* no observer */ }
    return () => { window.removeEventListener('resize', read); if (ro) ro.disconnect(); };
  }, [read, ref]);
  return width;
}

/**
 * Resolve one width strategy for a column set.
 * Returns the columns with pixel widths, the scrollX antd should get, and the table layout.
 */
function resolveGeometry(columns, { containerWidth, scrollX, tableLayout, gutterWidth }) {
  const hasFixedCol = columns.some((c) => !!c.fixed);
  const avail = containerWidth > 0 ? Math.max(containerWidth - 2 - gutterWidth, 0) : 0; // 2 = container border
  const pcts = columns.map((c) => asPct(c.width));
  const pxs = columns.map((c, i) => (pcts[i] == null ? asPx(c.width) : null));
  const hasPct = pcts.some((v) => v != null);

  let widths = pxs;
  if (hasPct && avail > 0) {
    // Percentages are relative to each other, never trusted to sum to 100 — renormalise over the space
    // left by the columns that asked for pixels.
    const totalPct = pcts.reduce((s, v) => s + (v || 0), 0) || 100;
    const declaredPx = pxs.reduce((s, v) => s + (v || 0), 0);
    const autoCount = columns.filter((c, i) => pcts[i] == null && pxs[i] == null).length;
    const space = Math.max(avail - declaredPx - autoCount * AUTO_COL_WIDTH, MIN_COL_WIDTH * pcts.filter((v) => v != null).length);
    widths = columns.map((c, i) => (pcts[i] != null ? Math.max(Math.round((pcts[i] / totalPct) * space), MIN_COL_WIDTH) : pxs[i]));
  }

  const sized = columns.map((c, i) => (widths[i] != null && widths[i] !== c.width ? { ...c, width: widths[i] } : c));
  const allSized = columns.length > 0 && widths.every((w) => w != null);
  const needed = widths.reduce((s, w) => s + (w == null ? AUTO_COL_WIDTH : w), 0) + gutterWidth;

  let x = scrollX ?? (hasFixedCol ? 'max-content' : undefined);
  if (allSized) {
    // Every width is known, so ask for exactly what is needed instead of 'max-content' (which lets the
    // content stretch the table) — and for nothing at all when the columns already fit. This must not
    // depend on the measurement: flipping between 'max-content' (layout auto) and a number (layout
    // fixed) after the first paint leaves rc-table's header colgroup on the widths it measured while
    // the body was still in auto layout, which is a misalignment of its own.
    x = (avail > 0 && needed <= avail + 1 && !hasFixedCol) ? undefined : needed;
  } else if (hasPct && !hasFixedCol && avail > 0) {
    x = undefined; // % widths and a horizontal scrollbar can never agree
  }

  return { columns: sized, scrollX: x, tableLayout: tableLayout ?? (allSized && x !== 'max-content' ? 'fixed' : undefined) };
}

function exportRows(rows, columns, { filename = 'export', format = 'csv' }) {
  const cols = columns.filter((c) => c.exportable !== false && (c.dataIndex || c.exportValue));
  const header = cols.map(colTitle);
  const body = rows.map((r) => cols.map((c) => (c.exportValue ? c.exportValue(r) : cellText(getByIndex(r, c.dataIndex)))));
  const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
  if (format === 'xlsx') {
    return import('xlsx').then((XLSX) => {
      const ws = XLSX.utils.aoa_to_sheet([header, ...body]);
      const wb = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb, ws, 'Data');
      XLSX.writeFile(wb, `${filename}-${stamp}.xlsx`);
    });
  }
  const esc = (s) => { const t = String(s ?? ''); return /[",\n]/.test(t) ? `"${t.replace(/"/g, '""')}"` : t; };
  const csv = [header, ...body].map((r) => r.map(esc).join(',')).join('\n');
  const a = document.createElement('a'); a.href = URL.createObjectURL(new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8' })); a.download = `${filename}-${stamp}.csv`; a.click();
  return Promise.resolve();
}

/** Column visibility — same localStorage key useTableConfig used, so existing user choices survive. */
function useColumnVisibility(tableKey, columns) {
  // Key the defaults on a *signature*, not the array identity — most pages rebuild `columns` on
  // every render, and reacting to the identity would loop setState forever.
  const sig = columns.map((c, i) => `${colKey(c, i)}:${c.defaultVisible !== false || !!c.alwaysVisible ? 1 : 0}`).join('|');
  const defaults = useMemo(() => Object.fromEntries(sig ? sig.split('|').map((t) => { const at = t.lastIndexOf(':'); return [t.slice(0, at), t.slice(at + 1) === '1']; }) : []), [sig]);
  const [vis, setVis] = useState(() => (tableKey ? { ...defaults, ...storage.get(`${tableKey}_visibleColumns`, {}) } : defaults));
  useEffect(() => {
    setVis((v) => {
      const next = { ...defaults, ...(tableKey ? storage.get(`${tableKey}_visibleColumns`, {}) : {}) };
      Object.keys(v).forEach((k) => { if (k in defaults) next[k] = v[k]; });
      const same = Object.keys(next).length === Object.keys(v).length && Object.keys(next).every((k) => next[k] === v[k]);
      return same ? v : next;
    });
  }, [defaults, tableKey]);
  const toggle = useCallback((k, on) => setVis((v) => { const n = { ...v, [k]: on }; if (tableKey) storage.set(`${tableKey}_visibleColumns`, n); return n; }), [tableKey]);
  const reset = useCallback(() => { setVis(defaults); if (tableKey) storage.set(`${tableKey}_visibleColumns`, defaults); }, [defaults, tableKey]);
  const setAll = useCallback((map) => { setVis(map); if (tableKey) storage.set(`${tableKey}_visibleColumns`, map); }, [tableKey]);
  return { vis, toggle, reset, setAll };
}

export default function DataTable({
  tableKey,
  columns: rawColumns = [],
  data,
  dataSource,
  rowKey = 'key',
  title,
  toolbarExtra,
  showToolbar,
  searchable: searchableProp,
  searchKeys,
  searchPlaceholder = 'Search…',
  searchValue: controlledSearch,
  onSearch,
  columnConfig: columnConfigProp,
  showCount = false,        // the small row-count pill left of the toolbar — off everywhere by default; `showCount` turns it on
  filterContent,            // extra filter controls the page owns (e.g. a severity filter), shown at the top of the filter panel
  filterActiveCount = 0,    // how many of those are active — added to the funnel's badge
  onFilterClear,            // called by the panel's "Clear all filters" so the page can reset its own filters too
  filterPanel = false,     // a filter icon in the toolbar that opens a panel with one multi-select per filterable column
  columnPicker = 'menu',   // 'menu' = a dropdown with a column search and checkboxes; 'modal' = the "Customize Columns" dialog
  sortable = true,
  filterable = true,
  maxFilterValues = 40,
  exportable = false,
  exportFilename,
  onRefresh,
  height = 450,
  minHeight,
  pagination = true,
  bordered = true,   // cell borders (a thin line between columns) on every table; `bordered={false}` gives the lines-between-rows-only look
  size = 'small',
  scrollX,
  tableLayout,
  ellipsis = true,
  rowClassName,
  locale,
  className = '',
  onRow,
  loading,
  expandable,
  emptyMessage,
  emptyFilteredMessage,
  error,
  errorMessage,
  onRetry,
  serverSide = false,
  ...rest
}) {
  let isDark = false;
  try { isDark = !!useTheme()?.isDark; } catch (e) { /* rendered outside ThemeProvider (static Modal.info etc.) */ }
  const scopeClass = `dt-${useId().replace(/[:]/g, '')}`;
  const rows = useMemo(() => data ?? dataSource ?? [], [data, dataSource]);
  const boxRef = useRef(null);
  const containerWidth = useContainerWidth(boxRef);

  // ---- columns: visibility + declarative sorters
  const { vis, toggle, reset, setAll } = useColumnVisibility(tableKey, rawColumns);
  // Search, customize-columns and the filter panel are opt-in per table (`searchable`, `columnConfig`,
  // `filterPanel`); all are off unless a page asks. `columnConfig` stays on for a table with a `tableKey`,
  // as it always was, so the choice can be remembered.
  const searchable = searchableProp ?? false;
  const columnConfig = columnConfigProp ?? !!tableKey;
  const [filteredInfo, setFilteredInfo] = useState({});
  const [sortedInfo, setSortedInfo] = useState({});
  const columns = useMemo(() => rawColumns
    .filter((c, i) => c.alwaysVisible || vis[colKey(c, i)] !== false)
    .map((c, i) => {
      const k = colKey(c, i);
      const out = { ...c };
      // sorting: explicit `sortType` wins; otherwise auto-sort every data column unless col.sortable === false
      if (!serverSide && !out.sorter && out.dataIndex && out.sortable !== false && (out.sortType || sortable) && !isActionColumn(out)) {
        out.sorter = sorterFor(out.sortType || inferSortType(rows, out.dataIndex), out.dataIndex);
      }
      if (out.sorter) out.sortOrder = sortedInfo.columnKey === k ? sortedInfo.order : null;
      if (!out.align && out.dataIndex && (out.sortType === 'number' || (!out.sortType && sortable && !isActionColumn(out) && inferSortType(rows, out.dataIndex) === 'number'))) { out.align = 'right'; out.className = `${out.className || ''} dt-num`.trim(); }
      // long text truncates to one line (with a title tooltip) instead of pushing the row taller, so
      // every row is the same height — opt out with `ellipsis: false` on the column or the table
      if (ellipsis && out.ellipsis === undefined && out.width != null && out.dataIndex && !out.render && !out.children && !isActionColumn(out)) out.ellipsis = { showTitle: true };
      // filters: explicit antd `filters` kept; `filterable: true` (or auto when few distinct values) → value list from the data
      if (!serverSide && !out.filters && out.dataIndex && out.filterable !== false && (out.filterable || filterable) && !isActionColumn(out)) {
        const values = new Map();
        for (const r of rows) { const t = cellText(getByIndex(r, out.dataIndex)); if (t !== '' && !values.has(t)) values.set(t, true); if (values.size > maxFilterValues) break; }
        if (values.size > 1 && (out.filterable || values.size <= maxFilterValues)) {
          out.filters = [...values.keys()].sort((a, b) => sorterFor(inferSortType(rows, out.dataIndex), 'v')({ v: a }, { v: b })).map((v) => ({ text: v, value: v }));
          out.filterSearch = true;
          out.onFilter = (val, r) => cellText(getByIndex(r, out.dataIndex)) === val;
        }
      }
      if (out.filters) out.filteredValue = filteredInfo[k] || null;
      out.key = k;
      return out;
    }), [rawColumns, vis, rows, sortable, filterable, maxFilterValues, filteredInfo, sortedInfo, ellipsis, serverSide]);
  const activeFilters = Object.entries(filteredInfo).filter(([, v]) => v && v.length);
  // toolbar filter panel: one multi-select per column that has a value filter, writing the same state the header
  // filter icons and the chips use, so the three always agree
  const filterCols = columns.filter((c) => c.filters && c.filters.length);
  const activeFilterCount = activeFilters.length + (filterActiveCount || 0);
  const filterPanelContent = (
    <div style={{ width: 280, maxHeight: 360, overflowY: 'auto' }}>
      {filterContent && <div style={{ marginBottom: filterCols.length ? 12 : 4, paddingBottom: filterCols.length ? 10 : 0, borderBottom: filterCols.length ? '1px solid rgba(148,163,184,.3)' : 'none' }}>{filterContent}</div>}
      {filterCols.map((c) => (
        <div key={c.key} style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>{typeof c.title === 'string' ? c.title : (c.exportTitle || c.key)}</div>
          <Select mode="multiple" allowClear size="small" showSearch optionFilterProp="label" maxTagCount="responsive" placeholder="All" style={{ width: '100%' }}
            value={filteredInfo[c.key] || []} options={c.filters.map((f) => ({ value: f.value, label: String(f.text) }))}
            onChange={(vals) => setFilteredInfo((f) => ({ ...f, [c.key]: vals.length ? vals : null }))} />
        </div>
      ))}
      <div className="flex justify-end"><Button type="link" size="small" style={{ padding: 0 }} disabled={!activeFilterCount} onClick={() => { setFilteredInfo({}); onFilterClear?.(); }}>Clear all filters</Button></div>
    </div>
  );
  const clearFiltersAndSort = () => { setFilteredInfo({}); setSortedInfo({}); };
  const handleTableChange = (pg, filters, sorter, extra) => {
    setFilteredInfo(filters || {});
    const s1 = Array.isArray(sorter) ? sorter[0] : sorter;
    setSortedInfo(s1 && s1.order ? { columnKey: s1.columnKey, order: s1.order } : {});
    rest.onChange?.(pg, filters, sorter, extra);
  };

  // ---- search (client side unless onSearch is given)
  const [localSearch, setLocalSearch] = useState('');
  const search = controlledSearch ?? localSearch;
  const filtered = useMemo(() => {
    if (!searchable || !search || onSearch) return rows;
    const q = search.toLowerCase();
    const keys = searchKeys || rawColumns.map((c) => c.dataIndex).filter(Boolean);
    return rows.filter((r) => keys.some((k) => cellText(getByIndex(r, k)).toLowerCase().includes(q)));
  }, [rows, search, searchable, searchKeys, onSearch, rawColumns]);

  // ---- pagination (page size persisted per tableKey)
  const [pageSize, setPageSize] = useState(() => (tableKey ? storage.get(`${tableKey}_pageSize`, null) : null) || (pagination && pagination.pageSize) || DEFAULT_PAGE_SIZE);
  const [page, setPage] = useState(1);
  useEffect(() => { if (!serverSide) setPage(1); }, [search, rows.length, serverSide]);   // server mode: the parent owns the page
  const paginationConfig = !pagination ? false : {
    showSizeChanger: true, pageSizeOptions: PAGE_SIZE_OPTIONS, size: 'small',
    showTotal: (total, range) => <span className="dt-total">{range[0]}–{range[1]} of {total}</span>,
    ...(pagination === true ? {} : pagination),
    pageSize: serverSide ? (pagination.pageSize ?? pageSize) : (pagination && pagination.pageSize && !tableKey) ? pagination.pageSize : pageSize,
    current: (pagination && pagination.current) ?? page,
    // server paging + client-side search only narrows the loaded page, so the pager must count what is shown
    ...(serverSide && searchable && search && !onSearch ? { total: filtered.length, current: 1 } : {}),
    onChange: (p, ps) => { setPage(p); if (ps !== pageSize) { setPageSize(ps); if (tableKey) storage.set(`${tableKey}_pageSize`, ps); } pagination?.onChange?.(p, ps); },
  };

  // ---- layout: one width strategy for header / body / summary (see resolveGeometry)
  const isEmpty = filtered.length === 0;
  const totalRows = serverSide && pagination && pagination.total != null ? pagination.total : rows.length;
  const gutterWidth = (rest.rowSelection ? (asPx(rest.rowSelection.columnWidth) ?? GUTTER_COL_WIDTH) : 0)
    + (expandable && (expandable.expandedRowRender || expandable.rowExpandable) ? GUTTER_COL_WIDTH : 0);
  const geometry = useMemo(
    () => resolveGeometry(columns, { containerWidth, scrollX, tableLayout, gutterWidth }),
    [columns, containerWidth, scrollX, tableLayout, gutterWidth],
  );
  const sizedColumns = geometry.columns;
  const effectiveScrollX = isEmpty ? undefined : geometry.scrollX;
  // what the toolbar rule was before search + columns became the default; a toolbar that now appears where
  // none did gives its height back from the rows box, so a fixed-height table keeps its footprint
  const legacyToolbarOn = showToolbar ?? (!!title || !!toolbarExtra || searchableProp === true || (columnConfigProp ?? !!tableKey) || filterPanel || exportable || !!onRefresh || activeFilters.length > 0 || !!sortedInfo.order);
  const toolbarOn = showToolbar ?? (legacyToolbarOn || searchable || columnConfig || filterPanel);
  const TOOLBAR_RESERVED_HEIGHT = 38;
  const autoToolbarHeight = toolbarOn && !legacyToolbarOn ? TOOLBAR_RESERVED_HEIGHT : 0;
  const noHeightCap = height === null;
  const rowsBoxHeight = noHeightCap ? undefined : !paginationConfig ? (typeof height === 'number' ? height - autoToolbarHeight : autoToolbarHeight ? `calc(${height} - ${autoToolbarHeight}px)` : height) : typeof height === 'number' ? height - PAGINATION_RESERVED_HEIGHT - autoToolbarHeight : `calc(${height} - ${PAGINATION_RESERVED_HEIGHT + autoToolbarHeight}px)`;
  const rowsBoxHeightCss = typeof rowsBoxHeight === 'number' ? `${rowsBoxHeight}px` : rowsBoxHeight;
  const minHeightCss = typeof minHeight === 'number' ? `${minHeight}px` : minHeight;

  // ---- skeleton on first load
  const isInitialLoading = !!loading && rows.length === 0;
  const skeletonBar = isDark ? '#334155' : '#e5e7eb';
  const renderColumns = isInitialLoading ? sizedColumns.map((c) => ({ ...c, sorter: undefined, filters: undefined, onFilter: undefined, render: () => <div style={{ height: 14, borderRadius: 4, background: skeletonBar, animation: 'dt-skeleton-pulse 1.4s ease-in-out infinite' }} /> })) : sizedColumns;
  const renderData = isInitialLoading ? Array.from({ length: SKELETON_ROW_COUNT }, (_, i) => ({ __dtSkeletonKey: i })) : filtered;

  // Percentage widths are now resolved to pixels, but a table that mixes them with a *content-driven*
  // scrollX still can't be sized (there is no container width to take a percentage of), so keep warning.
  const warned = useRef(false);
  useEffect(() => {
    if (process.env.NODE_ENV === 'production' || warned.current || geometry.scrollX !== 'max-content') return;
    const pct = rawColumns.filter((c) => asPct(c.width) != null);
    if (pct.length) { warned.current = true; console.warn(`[DataTable] percentage widths cannot be resolved against scrollX="max-content"; give these columns pixel widths: ${pct.map((c) => colTitle(c)).join(', ')}`); }
  }, [geometry.scrollX, rawColumns]);

  // ---- empty / error state: loading shows the skeleton or spinner, never this; error and "nothing
  // here yet" are told apart; a module-specific message replaces the generic one when given
  const narrowed = !!search || activeFilters.length > 0;
  const emptyText = error
    ? (errorMessage || 'We couldn’t load this data. Please try again.')
    : narrowed
      ? (emptyFilteredMessage || (emptyMessage ? `${emptyMessage.replace(/\s+found$/i, '')} match your search or filters` : search ? `No rows match “${search}”` : 'No rows match the filters'))
      : (emptyMessage || 'No data');
  const emptyNode = (
    <Empty
      image={error ? <ExclamationCircleOutlined style={{ fontSize: 40, color: '#dc2626' }} /> : Empty.PRESENTED_IMAGE_SIMPLE}
      description={emptyText}
    >
      {error && onRetry ? <Button size="small" type="primary" icon={<ReloadOutlined />} onClick={onRetry}>Retry</Button> : null}
    </Empty>
  );

  // ---- toolbar
  const [exporting, setExporting] = useState(false);
  const doExport = (format) => { setExporting(true); exportRows(filtered, columns, { filename: exportFilename || tableKey || 'export', format }).finally(() => setExporting(false)); };
  // "Customize Columns" dialog: every column is a switch (all on by default; a column a page marks
  // `defaultVisible: false` starts off). Changes are a draft until "Apply Changes"; Cancel drops them.
  // A column a page marks `alwaysVisible` can't be switched off, and at least one column must stay on.
  const [colModalOpen, setColModalOpen] = useState(false);
  const [draft, setDraft] = useState({});
  const colEntries = rawColumns.map((c, i) => ({ k: colKey(c, i), c }));
  const openColModal = () => { setDraft(Object.fromEntries(colEntries.map(({ k, c }) => [k, !!c.alwaysVisible || vis[k] !== false]))); setColModalOpen(true); };
  const draftOn = (k, c) => !!c.alwaysVisible || draft[k] !== false;
  const draftCount = colEntries.filter(({ k, c }) => draftOn(k, c)).length;
  const draftShowAll = () => setDraft(Object.fromEntries(colEntries.map(({ k }) => [k, true])));
  const draftHideAll = () => setDraft(Object.fromEntries(colEntries.map(({ k, c }) => [k, !!c.alwaysVisible])));
  // dropdown version: a column search, one checkbox per column (all ticked by default), changes apply at once
  const [colSearch, setColSearch] = useState('');
  const liveOn = (k, c) => !!c.alwaysVisible || vis[k] !== false;
  const liveCount = colEntries.filter(({ k, c }) => liveOn(k, c)).length;
  const colMenu = (
    <div style={{ width: 240 }}>
      <Input size="small" allowClear prefix={<SearchOutlined style={{ color: '#94a3b8' }} />} placeholder="Search column…" aria-label="Search column" value={colSearch} onChange={(e) => setColSearch(e.target.value)} style={{ marginBottom: 8 }} />
      <div style={{ maxHeight: 280, overflowY: 'auto' }}>
        {colEntries.filter(({ c }) => colTitle(c).toLowerCase().includes(colSearch.trim().toLowerCase())).map(({ k, c }) => {
          const on = liveOn(k, c);
          return (
            <div key={k} style={{ padding: '4px 2px' }}>
              <Checkbox checked={on} disabled={!!c.alwaysVisible || (on && liveCount <= 1)} onChange={(e) => toggle(k, e.target.checked)}>{colTitle(c)}</Checkbox>
            </div>
          );
        })}
        {colEntries.every(({ c }) => !colTitle(c).toLowerCase().includes(colSearch.trim().toLowerCase())) && <div style={{ padding: 8, fontSize: 12, color: '#94a3b8' }}>No column matches</div>}
      </div>
      <div className="flex items-center justify-between" style={{ marginTop: 8, paddingTop: 6, borderTop: '1px solid rgba(148,163,184,.3)', fontSize: 12 }}>
        <Button type="link" size="small" style={{ padding: 0 }} onClick={() => setAll(Object.fromEntries(colEntries.map(({ k }) => [k, true])))}>Select all</Button>
        <span style={{ color: '#94a3b8' }}>{liveCount} of {colEntries.length}</span>
        <Button type="link" size="small" style={{ padding: 0 }} onClick={reset}>Reset</Button>
      </div>
    </div>
  );
  const applyColumns = () => { setAll(Object.fromEntries(colEntries.map(({ k, c }) => [k, draftOn(k, c)]))); setColModalOpen(false); };

  const T = isDark
    ? { headerBg: '#1f2937', headerText: '#cbd5e1', text: '#e5e7eb', muted: '#94a3b8', border: '#334155', rowHover: '#1e293b', zebra: 'transparent', accent: '#38bdf8', accentSoft: 'rgba(56,189,248,.14)', surface: defaultColors.palette.gray[800], sb: '#475569', sbHover: '#64748b', pill: '#334155' }
    : { headerBg: '#f8fafc', headerText: '#475569', text: '#1f2937', muted: '#64748b', border: '#e2e8f0', rowHover: '#f1f5f9', zebra: 'transparent', accent: '#0e6c7a', accentSoft: 'rgba(14,108,122,.10)', surface: '#ffffff', sb: '#cbd5e1', sbHover: '#94a3b8', pill: '#eef2f7' };
  return (
    <div ref={boxRef} className={`${scopeClass} ${className}`.trim()} role="region" aria-label={typeof title === 'string' ? title : undefined} aria-busy={!!loading}>
      {toolbarOn && (
        <div className="dt-toolbar" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
          <Space size={8} wrap>
            {title && <Typography.Text strong style={{ fontSize: 14 }}>{title}</Typography.Text>}
            {showCount && !isInitialLoading && <span className="dt-count">{!serverSide && search && filtered.length !== rows.length ? `${filtered.length} of ${rows.length}` : totalRows}</span>}
            {activeFilters.map(([k, v]) => { const c = rawColumns.find((x, i) => colKey(x, i) === k); return <Tag key={k} closable onClose={() => setFilteredInfo((f) => ({ ...f, [k]: null }))} style={{ margin: 0 }}>{c ? colTitle(c) : k}: {v.join(', ')}</Tag>; })}
            {(activeFilters.length > 0 || sortedInfo.order) && <Button type="link" size="small" style={{ padding: 0, height: 'auto' }} onClick={clearFiltersAndSort}>clear {activeFilters.length ? 'filters' : ''}{activeFilters.length && sortedInfo.order ? ' & ' : ''}{sortedInfo.order ? 'sort' : ''}</Button>}
          </Space>
          <Space size={6} wrap>
            {searchable && <Input allowClear size="small" prefix={<SearchOutlined style={{ color: '#94a3b8' }} />} placeholder={searchPlaceholder} value={search} onChange={(e) => { const v = e.target.value; if (onSearch) onSearch(v); else setLocalSearch(v); }} onPressEnter={(e) => onSearch?.(e.target.value)} style={{ width: 240 }} className="dt-search" />}
            {toolbarExtra}
            {onRefresh && <Tooltip title="Refresh"><Button size="small" aria-label="Refresh" icon={<ReloadOutlined />} onClick={onRefresh} loading={!!loading && rows.length > 0} /></Tooltip>}
            {exportable && <Dropdown menu={{ items: [{ key: 'csv', label: 'Export CSV' }, { key: 'xlsx', label: 'Export Excel' }], onClick: ({ key }) => doExport(key) }} trigger={['click']}><Button size="small" icon={<DownloadOutlined />} loading={exporting} disabled={isEmpty}>Export</Button></Dropdown>}
            {filterPanel && (filterCols.length > 0 || !!filterContent) && <Popover trigger="click" placement="bottomRight" arrow={false} content={filterPanelContent}><Tooltip title="Filter"><Badge count={activeFilterCount} size="small" offset={[-2, 2]}><Button size="small" aria-label="Filter" icon={<Funnel size={14} />} type={activeFilterCount ? 'primary' : 'default'} ghost={!!activeFilterCount} /></Badge></Tooltip></Popover>}
            {columnConfig && (columnPicker === 'modal'
              ? <Tooltip title="Customize columns"><Button size="small" aria-label="Customize columns" icon={<SettingOutlined />} onClick={openColModal} /></Tooltip>
              : <Popover trigger="click" placement="bottomRight" arrow={false} content={colMenu} onOpenChange={(o) => { if (!o) setColSearch(''); }}><Tooltip title="Customize columns"><Button size="small" aria-label="Customize columns" icon={<SettingOutlined />} /></Tooltip></Popover>)}
          </Space>
        </div>
      )}
      <Table
        size={size}
        tableLayout={geometry.tableLayout}
        pagination={isInitialLoading ? false : paginationConfig}
        columns={renderColumns}
        dataSource={renderData}
        rowKey={isInitialLoading ? '__dtSkeletonKey' : rowKey}
        expandable={isInitialLoading ? undefined : expandable}
        rowClassName={isInitialLoading ? undefined : rowClassName}
        onRow={isInitialLoading ? undefined : onRow}
        loading={isInitialLoading ? false : loading}
        scroll={effectiveScrollX || rowsBoxHeight ? { x: effectiveScrollX, y: rowsBoxHeight } : undefined}
        locale={{ emptyText: emptyNode, ...locale }}
        {...rest}
        onChange={handleTableChange}
      />
      {columnPicker === 'modal' && <Modal
        open={colModalOpen}
        onCancel={() => setColModalOpen(false)}
        width={720}
        centered
        destroyOnHidden
        title={(
          <div className="flex items-center gap-3">
            <LayoutGrid size={22} aria-hidden style={{ color: T.muted }} />
            <div>
              <div style={{ fontSize: 16, fontWeight: 600, color: T.text }}>Customize Columns</div>
              <div style={{ fontSize: 12, fontWeight: 400, color: T.muted }}>Choose which columns are visible in the table</div>
            </div>
          </div>
        )}
        footer={(
          <div className="flex items-center justify-between">
            <Button type="link" style={{ padding: 0 }} onClick={() => setDraft(Object.fromEntries(colEntries.map(({ k, c }) => [k, c.defaultVisible !== false || !!c.alwaysVisible])))}>Reset to default</Button>
            <Space>
              <Button onClick={() => setColModalOpen(false)}>Cancel</Button>
              <Button type="primary" disabled={draftCount === 0} onClick={applyColumns}>Apply Changes</Button>
            </Space>
          </div>
        )}
      >
        <div className="flex items-center justify-between" style={{ margin: '8px 0 12px', fontSize: 13, color: T.text }}>
          <span>{draftCount} of {colEntries.length} columns visible</span>
          <span>
            <Button type="link" size="small" style={{ padding: 0 }} onClick={draftShowAll}>Show all</Button>
            <span style={{ margin: '0 8px', color: T.muted }}>|</span>
            <Button type="link" size="small" style={{ padding: 0 }} onClick={draftHideAll}>Hide all</Button>
          </span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 10, maxHeight: '50vh', overflowY: 'auto', paddingRight: 4 }}>
          {colEntries.map(({ k, c }) => {
            const on = draftOn(k, c);
            const locked = !!c.alwaysVisible;
            return (
              <div
                key={k}
                role="button"
                tabIndex={0}
                aria-pressed={on}
                onClick={() => !locked && setDraft((d) => ({ ...d, [k]: !on }))}
                onKeyDown={(e) => { if (!locked && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); setDraft((d) => ({ ...d, [k]: !on })); } }}
                className="flex items-center justify-between"
                style={{ padding: '9px 14px', borderRadius: 8, cursor: locked ? 'not-allowed' : 'pointer', border: `1px solid ${on ? T.border : 'transparent'}`, background: on ? T.accentSoft : 'transparent', transition: 'background .15s, border-color .15s' }}
              >
                <span className="flex items-center gap-2" style={{ color: on ? T.text : T.muted, fontSize: 13, minWidth: 0 }}>
                  {on ? <Eye size={15} aria-hidden /> : <EyeOff size={15} aria-hidden />}
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{colTitle(c)}</span>
                </span>
                <Switch size="small" checked={on} disabled={locked} aria-label={`Show ${colTitle(c)}`} />
              </div>
            );
          })}
        </div>
      </Modal>}
      <style>{`
        .${scopeClass} { --dt-accent: ${T.accent}; }
        .${scopeClass} .dt-toolbar { min-height: 30px; }
        .${scopeClass} .dt-count { display: inline-block; font-size: 11px; font-weight: 600; letter-spacing: .02em; color: ${T.muted}; background: ${T.pill}; border-radius: 999px; padding: 1px 8px; font-variant-numeric: tabular-nums; }
        .${scopeClass} .dt-search .ant-input { font-size: 12px; }
        .${scopeClass} .ant-table { font-size: 12.5px; color: ${T.text}; background: transparent; }
        .${scopeClass} .ant-table-container { border: 1px solid ${T.border}; border-radius: 8px; overflow: hidden; }
        .${scopeClass} .ant-table-thead > tr > th, .${scopeClass} .ant-table-thead > tr > td {
          background: ${T.headerBg} !important; color: ${T.headerText} !important; font-weight: 600 !important; font-size: 13.5px; letter-spacing: .01em;
          white-space: nowrap; border-bottom: 1px solid ${T.border} !important; padding: 11px 10px !important;
          /* Guarantees the header paints above body content (e.g. a briefly mismeasured expanded row) — matches antd's own z-index scale for fixed columns (2), so it doesn't fight that instead. */
          position: relative; z-index: 2;
        }
        .${scopeClass} .ant-table-thead > tr > th::before { display: none !important; }
        .${scopeClass} .ant-table-thead th.ant-table-column-has-sorters:hover { background: ${T.rowHover} !important; }
        /* table-layout: fixed constrains a cell's own box width but never forces its content to wrap to fit — long unbroken text (or several inline elements packed tight, e.g. Tags) just overflows past the cell into whatever's next instead. */
        .${scopeClass} .ant-table-tbody > tr > td { padding: 6px 10px !important; border-bottom: 1px solid ${T.border} !important; vertical-align: middle; word-break: break-word; overflow-wrap: anywhere; }
        /* word-break above has no effect once white-space: nowrap is set, and antd's Button/Tag/Typography — the usual custom-render content — all set it by default. Reset it back for the common cases so they wrap in their own cell instead of overflowing into the next. */
        .${scopeClass} .ant-table-tbody > tr > td .ant-btn, .${scopeClass} .ant-table-tbody > tr > td .ant-tag, .${scopeClass} .ant-table-tbody > tr > td .ant-typography { white-space: normal !important; }
        .${scopeClass} .ant-table-tbody > tr:last-child > td { border-bottom: none !important; }
        .${scopeClass} .ant-table-tbody > tr:hover > td { background: ${T.rowHover} !important; }
        .${scopeClass} td.dt-num, .${scopeClass} th.dt-num { font-variant-numeric: tabular-nums; }
        /* summary row: same grid, same padding, same figures as the body it totals */
        .${scopeClass} .ant-table-summary > tr > td, .${scopeClass} .ant-table-summary > tr > th {
          padding: 6px 10px !important; border-top: 1px solid ${T.border} !important; border-bottom: none !important;
          background: ${T.headerBg} !important; color: ${T.text}; font-weight: 600; font-variant-numeric: tabular-nums;
        }
        .${scopeClass} .ant-table-summary td.dt-num { font-variant-numeric: tabular-nums; }
        .${scopeClass} .ant-table-column-sort { background: transparent !important; }
        .${scopeClass} td.ant-table-column-sort { background: ${T.accentSoft} !important; }
        /* sort indicator: hidden until hover; a single arrow when active */
        .${scopeClass} .ant-table-column-sorter { opacity: 0; margin-inline-start: 2px; transition: opacity .15s; color: ${T.muted}; }
        .${scopeClass} .ant-table-column-sorter-inner { display: inline-flex; flex-direction: column; line-height: 1; }
        .${scopeClass} .ant-table-column-sorter .anticon { font-size: 9px; }
        .${scopeClass} th:hover .ant-table-column-sorter { opacity: .55; }
        .${scopeClass} th.ant-table-column-sort .ant-table-column-sorter { opacity: 1; }
        .${scopeClass} th.ant-table-column-sort .ant-table-column-sorter-up:not(.active),
        .${scopeClass} th.ant-table-column-sort .ant-table-column-sorter-down:not(.active) { display: none; }
        .${scopeClass} .ant-table-column-sorter-up.active, .${scopeClass} .ant-table-column-sorter-down.active { color: var(--dt-accent); font-size: 11px; }
        /* a table with the toolbar funnel (filterPanel) filters there — the per-column funnel in each header is not drawn */
        ${filterPanel ? `.${scopeClass} .ant-table-filter-trigger { display: none !important; }` : ''}
        /* filter trigger: hidden until hover; accent when a filter is applied */
        .${scopeClass} .ant-table-filter-trigger { opacity: 0; margin-inline: 2px -4px; border-radius: 4px; color: ${T.muted}; transition: opacity .15s; }
        .${scopeClass} th:hover .ant-table-filter-trigger { opacity: .6; }
        .${scopeClass} .ant-table-filter-trigger:hover { opacity: 1 !important; background: ${T.accentSoft}; color: var(--dt-accent); }
        .${scopeClass} .ant-table-filter-trigger.active { opacity: 1; color: var(--dt-accent); background: ${T.accentSoft}; }
        .${scopeClass} .ant-table-filter-column { justify-content: flex-start; gap: 2px; }
        .${scopeClass} .ant-table-column-title { flex: 0 1 auto; }
        /* A sortable/filterable header wraps its title in a flex row that antd always
           left-aligns (justify-content above), regardless of the column's own align — so a
           right-aligned numeric column (see 'dt-num' below) ends up with right-aligned data
           under a left-aligned header. Match the header's flex alignment to the column's. */
        .${scopeClass} th.dt-num .ant-table-filter-column,
        .${scopeClass} th.dt-num .ant-table-column-sorters { justify-content: flex-end !important; }
        /* selection + expand */
        .${scopeClass} .ant-table-selection-column { padding-inline: 8px !important; }
        .${scopeClass} .ant-table-row-expand-icon { border-color: ${T.border}; }
        /* pagination */
        .${scopeClass} .ant-pagination { margin: 10px 0 0 !important; display: flex; align-items: center; }
        .${scopeClass} .ant-pagination .dt-total { font-size: 12px; color: ${T.muted}; font-variant-numeric: tabular-nums; }
        .${scopeClass} .ant-pagination-total-text { margin-inline-end: auto; }
        .${scopeClass} .ant-pagination-item, .${scopeClass} .ant-pagination-prev, .${scopeClass} .ant-pagination-next { font-size: 12px; }
        .${scopeClass} .ant-pagination-item-active { border-color: var(--dt-accent); }
        .${scopeClass} .ant-pagination-item-active a { color: var(--dt-accent); }
        .${scopeClass} .ant-pagination .ant-select-selector { font-size: 12px; }
        /* empty + skeleton */
        .${scopeClass} .ant-empty-description { color: ${T.muted}; font-size: 12px; }
        ${minHeightCss != null ? `.${scopeClass} .ant-table-container { min-height: ${minHeightCss}; }` : ''}
        ${isEmpty ? `.${scopeClass} .ant-table-body, .${scopeClass} .ant-table-content, .${scopeClass} .ant-table-container { overflow: hidden !important; }` : ''}
        ${!noHeightCap ? `
        .${scopeClass} .ant-table-body { min-height: ${rowsBoxHeightCss} !important; }
        .${scopeClass} .ant-table-placeholder { height: ${rowsBoxHeightCss}; max-height: ${rowsBoxHeightCss}; overflow: hidden; }
        .${scopeClass} .ant-table-placeholder > td { height: ${rowsBoxHeightCss} !important; max-height: ${rowsBoxHeightCss} !important; overflow: hidden; vertical-align: middle; border-bottom: none !important; }` : ''}
        ${noHeightCap && minHeightCss != null ? `.${scopeClass} .ant-table-placeholder, .${scopeClass} .ant-table-placeholder > td { overflow: hidden; }` : ''}
        ${bordered ? `.${scopeClass} .ant-table-tbody > tr > td, .${scopeClass} .ant-table-thead > tr > th { border-inline-end: 1px solid ${T.border} !important; } .${scopeClass} .ant-table-tbody > tr > td:last-child, .${scopeClass} .ant-table-thead > tr > th:last-child { border-inline-end: none !important; }` : ''}
        .${scopeClass} .ant-table-body, .${scopeClass} .ant-table-content { scrollbar-width: thin; scrollbar-color: ${T.sb} transparent; }
        .${scopeClass} .ant-table-body::-webkit-scrollbar, .${scopeClass} .ant-table-content::-webkit-scrollbar { height: 6px; width: 6px; }
        .${scopeClass} .ant-table-body::-webkit-scrollbar-thumb, .${scopeClass} .ant-table-content::-webkit-scrollbar-thumb { background: ${T.sb}; border-radius: 4px; }
        .${scopeClass} .ant-table-body::-webkit-scrollbar-thumb:hover, .${scopeClass} .ant-table-content::-webkit-scrollbar-thumb:hover { background: ${T.sbHover}; }
        @keyframes dt-skeleton-pulse { 0%, 100% { opacity: 1; } 50% { opacity: .4; } }
      `}</style>
    </div>
  );
}

/** Table inside a modal — replaces TableInModal. */
export function DataTableModal({ open, isVisible, title, onOk, handleOk, onCancel, handleCancel, width = 800, data, sourceData, columns, columnsData, ...tableProps }) {
  return (
    <Modal open={open ?? isVisible} title={title} width={width} onOk={onOk ?? handleOk} onCancel={onCancel ?? handleCancel} footer={null} zIndex={1800} destroyOnHidden>
      <div className="mt-4">
        <DataTable data={data ?? sourceData} columns={columns ?? columnsData} pagination={false} height={null} {...tableProps} />
      </div>
    </Modal>
  );
}
DataTable.Modal = DataTableModal;
